import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FaDownload, FaExpand, FaTrash } from 'react-icons/fa';
import { deleteMedia } from '../../services/supabase';
import { IconButton } from '../Common/BaseButton';
import { X } from 'lucide-react';

const GalleryContainer = styled.div`
  width: 100%;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
  
  @media (max-width: 768px) {
    // grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
`;

const MediaCard = styled.div`
  position: relative;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
  will-change: transform;
  background-color: white;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const MediaPreview = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 100%;
  background: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;

  &:hover {
    .media-actions {
      opacity: 1;
    }
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transform: translateZ(0);
    backface-visibility: hidden;
    -webkit-font-smoothing: antialiased;
  }
`;

const MediaActions = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  will-change: opacity;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
`;

const ActionButton = styled.button`
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
  will-change: transform;

  &:hover {
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const MediaIcon = styled.div`
  font-size: 2rem;
`;

const MediaInfo = styled.div`
  padding: 0.8rem;
`;

const MediaTitle = styled.h4`
  margin: 0 0 0.3rem 0;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MediaMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #666;
`;

const DateDisplay = styled.span`
  font-size: 0.8rem;
  color: #666;
  display: inline-block;
  padding: 0.2rem 0.4rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 500;
  background-color:rgb(227, 227, 227);
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  max-width: 90vw;
  max-height: 90vh;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    max-width: 100%;
    max-height: 90vh;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }
`;

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric', 
    month: 'short', 
    day: 'numeric'
  });
};

const MediaGallery = ({ uploads = [], onDelete = () => {} }) => {
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // console.log('uploads', uploads);
  const handleDownload = async (upload) => {
    try {
      const link = document.createElement('a');
      link.href = upload.publicUrl;
      link.download = upload.media_url.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleDelete = async (upload) => {
    if (!window.confirm('Are you sure you want to delete this media?')) return;
    
    setDeleting(true);
    try {
      const { error } = await deleteMedia(upload.id, upload.media_url);
      if (error) throw error;
      
      onDelete(upload.id);
    } catch (error) {
      console.error('Error deleting media:', error);
      alert('Failed to delete media. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <GalleryContainer>
      <Grid>
        {uploads.map((upload) => (
          <MediaCard key={upload.id}>
            <MediaPreview>
              {upload?.file_type?.startsWith('image/') ? (
                <>
                  <img 
                    src={upload?.publicUrl} 
                    alt={upload?.original_filename?.split('/').pop()} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    loading="lazy"
                    decoding="async"
                  />
                  <MediaActions className="media-actions">
                    <ActionButton onClick={() => setSelectedMedia(upload)}>
                      <FaExpand />
                    </ActionButton>
                    <ActionButton onClick={() => handleDownload(upload)}>
                      <FaDownload />
                    </ActionButton>
                    <ActionButton 
                      onClick={() => handleDelete(upload)}
                      disabled={deleting}
                      style={{ backgroundColor: '#ffebee', color: '#c62828' }}
                    >
                      <FaTrash />
                    </ActionButton>
                  </MediaActions>
                </>
              ) : (
                <MediaIcon>
                  {upload.file_type.startsWith('video/') ? '🎬' : '📄'}
                </MediaIcon>
              )}
            </MediaPreview>
            <MediaInfo>
              <MediaTitle title={upload?.original_filename?.split('/').pop()}>
                {upload?.original_filename?.split('/').pop()}
              </MediaTitle>
              <MediaMeta>
                <div>
                  <DateDisplay>{formatDate(upload?.created_at)}</DateDisplay>
                </div>
              </MediaMeta>
            </MediaInfo>
          </MediaCard>
        ))}
      </Grid>

      {selectedMedia && createPortal(
        <Modal onClick={() => setSelectedMedia(null)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <IconButton style={{ position: 'absolute', top: '-35px', right: '0px' }} onClick={() => setSelectedMedia(null)}><X size={16} /></IconButton>
            {selectedMedia.file_type.startsWith('image/') ? (
              <img 
                src={selectedMedia.publicUrl} 
                alt={selectedMedia.original_filename.split('/').pop()} 
              />
            ) : (
              <div style={{ color: 'white', fontSize: '18px', padding: '20px' }}>
                Preview not available for this file type
              </div>
            )}
          </ModalContent>
        </Modal>,
        document.body
      )}
    </GalleryContainer>
  );
};

export default MediaGallery; 